/**
 * RequestExceptionModal — file an exception against the SCENARIO.
 *
 * `POST /pricing/scenarios/{id}/exception-requests` takes the same object a deal
 * exception does and routes to the same approvers, so an approval granted here
 * does not have to be granted again when the scenario becomes a loan — the row
 * re-parents on conversion. That is the whole reason this exists at scenario
 * stage rather than waiting for a deal id.
 *
 * The body is prefilled from the failing finding the button was on (its code,
 * message, citation, observed value and threshold), so the approver sees WHY
 * without opening the sizer. What the requester edits is the description and the
 * compensating factors — the argument, not the evidence.
 *
 * A scenario id is REQUIRED. A run persists one automatically, so the only way
 * to be here without one is a scenario that failed to store; the modal says so
 * rather than posting to `/scenarios/null`.
 */
import { useEffect, useState } from "react";

import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import InputWrapper from "../../../components/inputs/shared/InputWrapper";
import {
  SZ,
  SelectInput,
  PrimaryButton,
  SecondaryButton,
  ActionNote,
} from "../../sizersShared/sizerUi";
import { Callout } from "../../sizersShared/sizerResultUi";
import { selectOptionsExceptionCategory } from "../../../constants/selectOptions/selectOptionsExceptionCategory";
import { selectOptionsExceptionRiskBucket } from "../../../constants/selectOptions/selectOptionsExceptionRiskBucket";

const textAreaSx = {
  width: "100%",
  fontFamily: "inherit",
  fontSize: 13.5,
  color: SZ.ink900,
  background: SZ.card,
  border: `1px solid ${SZ.lineStrong}`,
  borderRadius: SZ.radiusSm,
  p: "10px 12px",
  resize: "vertical",
  outline: "none",
  "&:focus": {
    borderColor: SZ.teal500,
    boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
  },
};

// Label above the control, never a floating one — the house rule.
const labelSx = { fontSize: 12.5, fontWeight: 500, color: SZ.ink500 };

export default function RequestExceptionModal({
  open,
  onClose,
  onSubmit,
  scenarioId,
  prefill,
}) {
  const [form, setForm] = useState(prefill || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed whenever a DIFFERENT finding opens the modal. Keyed on the prefill
  // object's identity so reopening on the same finding keeps whatever the user
  // had already typed.
  useEffect(() => {
    if (open) {
      setForm(prefill || {});
      setError(null);
    }
  }, [open, prefill]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not file the request.");
    } finally {
      setSaving(false);
    }
  };

  const hard = form.severity === "hard";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: "18px 22px 6px" }}>
        <Txt component="div" sx={{ fontSize: 16, fontWeight: 700, color: SZ.ink900 }}>
          Request an exception
        </Txt>
        <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink500, mt: "3px" }}>
          Filed against this scenario. If it is approved before the deal is
          created, it carries onto the loan rather than gating processing again.
        </Txt>
      </Box>

      <Box sx={{ p: "12px 22px 0" }}>
        {!scenarioId && (
          <Callout tone="warn" title="No stored scenario" sx={{ mb: 2 }}>
            This run was not persisted, so there is nothing to attach a request
            to. Re-price the scenario and try again.
          </Callout>
        )}

        {(form.code || form.citation) && (
          <Box
            sx={{
              border: `1px solid ${SZ.line}`,
              borderRadius: SZ.radiusSm,
              background: "#fbfcfc",
              p: "10px 12px",
              mb: 2,
            }}
          >
            {form.code && (
              <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink400 }}>
                Finding{" "}
                <Box component="span" sx={{ fontFamily: SZ.mono, color: SZ.ink700 }}>
                  {form.code}
                </Box>
              </Txt>
            )}
            {form.observedValue != null && (
              <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink400, mt: "2px" }}>
                Observed{" "}
                <Box component="span" sx={{ fontFamily: SZ.mono, color: SZ.ink700 }}>
                  {String(form.observedValue)}
                </Box>
                {form.threshold != null && (
                  <>
                    {" "}
                    against{" "}
                    <Box component="span" sx={{ fontFamily: SZ.mono, color: SZ.ink700 }}>
                      {typeof form.threshold === "object"
                        ? JSON.stringify(form.threshold)
                        : String(form.threshold)}
                    </Box>
                  </>
                )}
              </Txt>
            )}
            {form.citation && (
              <Txt component="div" sx={{ fontSize: 11, color: SZ.ink400, mt: "2px" }}>
                {form.citation}
              </Txt>
            )}
          </Box>
        )}

        <Flx column g={2}>
          <Flx g={2} sx={{ flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
              <InputWrapper label="Category" labelSx={labelSx}>
                <SelectInput
                  value={form.exceptionCategory}
                  onChange={set("exceptionCategory")}
                  options={selectOptionsExceptionCategory}
                  placeholder="Select a category"
                />
              </InputWrapper>
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
              <InputWrapper label="Risk bucket" labelSx={labelSx}>
                <SelectInput
                  value={form.riskBucket}
                  onChange={set("riskBucket")}
                  options={selectOptionsExceptionRiskBucket}
                  placeholder="Select a risk bucket"
                />
              </InputWrapper>
            </Box>
          </Flx>

          <InputWrapper label="What is being requested" required labelSx={labelSx}>
            <Box
              component="textarea"
              rows={5}
              value={form.exceptionDescription || ""}
              onChange={(e) => set("exceptionDescription")(e.target.value)}
              sx={textAreaSx}
            />
          </InputWrapper>

          <InputWrapper
            label="Compensating factors"
            helperText="What makes this approvable. Seeded — edit before filing."
            labelSx={labelSx}
          >
            <Box
              component="textarea"
              rows={4}
              value={form.compensatingFactors || ""}
              onChange={(e) => set("compensatingFactors")(e.target.value)}
              sx={textAreaSx}
            />
          </InputWrapper>
        </Flx>

        {hard && (form.requiredDocuments || []).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Callout tone="warn" title="Filed as a hard exception">
              These become real conditions on the scenario and must be on file
              before the request can be reviewed:{" "}
              {form.requiredDocuments.join(" · ")}.
            </Callout>
          </Box>
        )}

        {error && (
          <Box sx={{ mt: 2 }}>
            <Callout tone="bad" title="Could not file the request">
              {error}
            </Callout>
          </Box>
        )}
      </Box>

      <Flx jb ac g={1.5} sx={{ p: "18px 22px 20px", flexWrap: "wrap" }}>
        <ActionNote>Approval authority is unchanged from a deal exception.</ActionNote>
        <Flx ac g={1.25}>
          <SecondaryButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={submit}
            disabled={saving || !scenarioId || !form.exceptionDescription}
            startIcon={
              saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null
            }
          >
            {saving ? "Filing…" : "File request"}
          </PrimaryButton>
        </Flx>
      </Flx>
    </Dialog>
  );
}
